import OcrDocumentModal from "../../../../../common/components/modal/ocr-document-modal";
import React, {useRef, useState} from "react";
import axios from "axios";
import Env from "../../../../../util/env";
import {getJWT} from "../../../../../util/util-auth";
import {pushNotification} from "../../../../../data/actions/ui";
import {uploadDocument} from "../../../../../data/actions/download";
import {useDispatch} from "react-redux";
import LocalStorage from "../../../../../util/localStorage";
import Resources from "../../../../../data/services/resources";
import InfoBar from "../../../../../common/components/info-paragraph/info-bar";
import {PencilIcon, TrashIcon, XCircleIcon} from "@heroicons/react/24/outline";
import {fieldsToHtml} from "../../../../../common/util/util-fields";
import Card from "../../../../../common/components/card";
import {FieldsManager} from "../../../../../data/services/fields";
import WorkOrdersLaborPartsPrices from "../work-orders-labor-parts-prices";
import {numberWithCommasToBack} from "../../../../../util/util-formaters";
import {genericMoneyFormatter} from "../../../../../common/util/util-vanilla";
import ResourceTable from "../../../../../common/components/resource-table";
import ModalSaveResource from "../../../../../common/components/modal/modal-save-resource";
import {DEFAULT_IMAGES_ACCEPTABLE_EXTENSIONS} from "../../../../../util/util-constants";

export default function WoLaborPartsOcr({
                                            show,
                                            onClose,
                                            ocrFile,
                                            id,
                                            getFieldsAction,
                                            getPartsFields,
                                            getLaborFields,
                                            getMiscFields,
                                            onSubmit,
                                            selects,
                                            translate,
                                            dataSettings
                                        }) {
    const abortControllerRef = useRef(null);
    const dispatch = useDispatch();

    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [previewData, setPreviewData] = useState(undefined); // Data that will be applied to the form
    const [originalData, setOriginalData] = useState(undefined); // For undo action
    const [items, setItems] = useState([]);
    const [isPartsFromDialogOpen, setIsPartsFormDialogOpen] = useState(false);
    const [isLaborFormDialogOpen, setIsLaborFormDialogOpen] = useState(false);
    const [isMiscFormDialogOpen, setIsMiscFormDialogOpen] = useState(false);

    const [selectedItem, setSelectedItem] = useState(undefined);
    const [isPartsFromDirty, setIsPartsFromDirty] = useState(false);
    const [selectedItemGroupIndex, setSelectedItemGroupIndex] = useState(-1);

    function handleClose() {
        onClose();

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
    }

    function handleInputChange(name, value, index, fields) {
        setItems(prevFields => {
            prevFields[index] = FieldsManager.updateField(fields, name, value);
            return [...prevFields];
        })
    }

    function handlePartsInputChange(fields, name, value) {
        fields = FieldsManager.updateField(fields, name, value);
        fields[name].errorMessage = "";
        return fields;
    }

    function handleLaborInputChange(fields, name, value) {
        fields = FieldsManager.updateField(fields, name, value);
        fields[name].errorMessage = "";
        return fields;
    }

    function handleUploadDocument(file) {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        const formData = new FormData();
        formData.append('Document', file);


        const isImage = DEFAULT_IMAGES_ACCEPTABLE_EXTENSIONS.includes(file.type);
        const query = isImage ? "?extractor=aws" : "";


        axios.post(
            Env.getDocumentAIApiUrl("api/" + Resources.AiExtractLaborPartsBill + query, {}),
            formData,
            {
                headers: {
                    Authorization: `Bearer ${getJWT().access_token}`,
                },
                signal: controller.signal
            }
        )
            .then((response) => {
                // const fileURL = window.URL.createObjectURL(new Blob([response.data]));
                if (response?.data?.data) {
                    const data = response.data.data;

                    if (data.TaxAmount) {
                        const allPerItemTaxSum = (data.Items ?? []).reduce((sum, item) => {
                            const partsTax = (item.Parts ?? []).reduce((s, p) => s + (Number(p.TaxAmount) || 0), 0);
                            const laborTax = (item.Labor ?? []).reduce((s, l) => s + (Number(l.TaxAmount) || 0), 0);
                            return sum + partsTax + laborTax;
                        }, 0);
                        const isTaxEmbedded = Math.abs(allPerItemTaxSum - data.TaxAmount) < 0.01;

                        if (!isTaxEmbedded) {
                            data.Misc = data.Misc ?? [];
                            data.Misc.push({
                                Description: "Tax",
                                MiscQty: 1,
                                MiscPrice: data.TaxAmount,
                                TaxAmount: 1,
                                TotalAmount: data.TaxAmount
                            });
                        }
                    }

                    setIsPreviewModalOpen(true);
                    setPreviewData(data);
                    setOriginalData(structuredClone(data));
                    setItems((data.Items ?? []).map(it => {
                        return getFieldsAction(it, false, dataSettings);
                    }));
                } else {
                    dispatch(pushNotification({
                        title: translate('Something went wrong while processing the document.'),
                        messageType: "error",
                        timeout: 16000
                    }));
                }
            })
            .catch((error) => {
                if (axios.isCancel(error)) {
                    console.log('Request was aborted.');
                } else {
                    dispatch(pushNotification({
                        title: translate('Something went wrong while processing the document.'),
                        messageType: "error",
                        timeout: 16000
                    }));

                    console.log("Error processing document:", error)
                    onClose();
                }
            })
    }

    function handleEditPartClick(item, index) {
        setIsPartsFormDialogOpen(true);
        setSelectedItem(item);
        setSelectedItemGroupIndex(index);
    }

    function handleEditLaborClick(item, index) {
        setIsLaborFormDialogOpen(true);
        setSelectedItem(item);
        setSelectedItemGroupIndex(index);
    }

    function handleDeletePartClick(item, index) {
        setPreviewData(prevData => {
            delete prevData?.Items[index].Parts.splice(item.dataIndex, 1);
            return Object.assign({}, prevData);
        })
    }

    function handleDeleteLaborClick(item, index) {
        setPreviewData(prevData => {
            delete prevData?.Items[index].Labor.splice(item.dataIndex, 1);
            return Object.assign({}, prevData);
        })
    }

    function handleDeleteGroupClick(index) {
        setPreviewData(prevData => {
            prevData.Items.splice(index, 1);
            return Object.assign({}, prevData);
        })
    }

    function handleEditMiscClick(item) {
        setIsMiscFormDialogOpen(true);
        setSelectedItem(item);
    }

    function handleDeleteMiscClick(item) {
        setPreviewData(prevData => {
            prevData.Misc.splice(item.dataIndex, 1);
            return Object.assign({}, prevData);
        })
    }

    function handleMiscInputChange(fields, name, value) {
        fields = FieldsManager.updateField(fields, name, value);
        fields[name].errorMessage = "";
        return fields;
    }

    const miscData = (previewData?.Misc ?? []).map(it => {
        it.TotalAmount = numberWithCommasToBack(it.MiscPrice) * (Number(it.MiscQty) ?? 0) * (Number(it.TaxAmount ? it.TaxAmount : 1));
        return it;
    });

    const miscTotal = miscData.reduce((sum, it) => sum + (it.TotalAmount || 0), 0);
    const miscQtyTotal = miscData.reduce((sum, it) => sum + (Number(it.MiscQty) || 0), 0);
    const miscPriceTotal = miscData.reduce((sum, it) => sum + (numberWithCommasToBack(it.MiscPrice) || 0), 0);

    const grandTotalParts = (previewData?.Items ?? []).reduce((sum, it) =>
        sum + (it.Parts ?? []).reduce((memo, p) => memo + (numberWithCommasToBack(p.TotalAmount) || 0), 0), 0);

    const grandTotalLabor = (previewData?.Items ?? []).reduce((sum, it) =>
        sum + (it.Labor ?? []).reduce((memo, l) => memo + (numberWithCommasToBack(l.TotalAmount) || 0), 0), 0);

    return <OcrDocumentModal
        formModalTitle={translate("text.add_ocr_data")}
        show={show}
        onSubmit={() => {
            onSubmit(previewData, items);
            dispatch(uploadDocument({
                user: LocalStorage.get("user"),
                files: [ocrFile],
                id: id,
                resource: Resources.WorkOrdersDocuments,
                piggyResource: Resources.WorkOrdersDocuments,
                piggyQuery: {searchFields: JSON.stringify({WorkOrderID: id})},
                successMessage: translate("text.document_uploaded"),
                errorMessage: true,
            }));
        }}
        footerCustomHtmlLeft={
            <InfoBar>This document will be uploaded and added to the Documents tab.</InfoBar>
        }
        submitButtonLabel={translate("text.save")}
        isPreviewModalOpen={isPreviewModalOpen}
        setIsPreviewModalOpen={setIsPreviewModalOpen}
        onFetchFormData={() => {
            setPreviewData(structuredClone(originalData));
        }}
        previewContent={
            <div className="p-6 space-y-4">
                <Card>
                    <h3 className="text-lg font-bold mb-2">Total</h3>
                    <WorkOrdersLaborPartsPrices
                        totalPriceParts={grandTotalParts}
                        totalPriceLabor={grandTotalLabor}
                        dataMiscTotal={miscTotal || undefined}
                    />
                </Card>


                {previewData?.Items.map((it, index) => {
                    let dataPartsTable = it.Parts;
                    dataPartsTable.forEach((element) => {
                        if (!element?.PartQty) {
                            element.PartQty = 1;
                        }
                        element.ind = index
                    });

                    const dataPartsTableTotal = dataPartsTable.reduce((memo, it) => memo + (numberWithCommasToBack(it.TotalAmount) || 0), 0);
                    const dataPartsQtyTotal = dataPartsTable.reduce((memo, it) => memo + (Number(it.PartQty) || 0), 0);
                    const dataPartsPriceTotal = dataPartsTable.reduce((memo, it) => memo + (numberWithCommasToBack(it.PartPrice) || 0), 0);

                    let dataLaborTable = it.Labor;
                    dataLaborTable.forEach((element) => {
                        if (!element?.LaborHours) {
                            element.LaborHours = 1;
                        }
                        element.ind = index
                    });

                    const dataLaborTableTotal = dataLaborTable.reduce((memo, it) => memo + (numberWithCommasToBack(it.TotalAmount) || 0), 0);
                    const dataLaborHoursTotal = dataLaborTable.reduce((memo, it) => memo + (Number(it.LaborHours) || 0), 0);
                    const dataLaborPriceTotal = dataLaborTable.reduce((memo, it) => memo + (numberWithCommasToBack(it.LaborPrice) || 0), 0);

                    return <Card key={index}>
                        {/*Action fields*/}
                        <div className={"flex items-center justify-between"}>
                            <h3 className={"text-lg font-bold "}>#{index + 1}</h3>

                            {previewData?.Items.length > 1 && (
                                <button
                                    className={"btn"}
                                    onClick={() => {
                                        handleDeleteGroupClick(index)
                                    }}
                                >
                                    <XCircleIcon className={"w-8 h-8"}/>
                                </button>
                            )}
                        </div>
                        <div className={"grid grid-cols-12 gap-x-6 gap-y-4"} id={`itemIndex${index}`}>
                            {fieldsToHtml(Object.values(Object.assign({}, items[index])), translate, (name, value) => handleInputChange(name, value, index, items[index]), selects)}
                        </div>

                        {/*Parts*/}
                        {(previewData?.Items?.length ?? 0) > 1 && (
                            <div className={"space-y-4"}>
                                <WorkOrdersLaborPartsPrices
                                    totalPriceParts={dataPartsTableTotal}
                                    totalPriceLabor={dataLaborTableTotal}
                                    totalLabel="Subtotal"
                                />
                            </div>
                        )}

                        <div className="space-y-8">
                            {!!dataPartsTable.length && (
                                <div className="space-y-4">
                                    <div className="text-base font-bold">Parts</div>

                                    <ResourceTable
                                        data={dataPartsTable}
                                        fields={getPartsFields({})}

                                        translate={translate}
                                        options={
                                            {
                                                behaviour: {
                                                    rowSelect: false,
                                                    canAdjustWidth: false,
                                                    hasMenu: false

                                                },
                                                style: {
                                                    condensed: true,
                                                    isGPUAccelerated: true,
                                                    horizontalLines: true,
                                                    verticalLines: true
                                                }
                                            }
                                        }
                                        tfoot={{
                                            PartQty: () => <div className="text-right font-bold">{dataPartsQtyTotal}</div>,
                                            PartPrice: () => <div className="text-right font-bold">{genericMoneyFormatter(dataPartsPriceTotal)}</div>,
                                            TotalAmount: () => <div className="text-right font-bold">{genericMoneyFormatter(dataPartsTableTotal)}</div>
                                        }}
                                        actions={[
                                            {
                                                title: () => translate('btn.edit'),
                                                action: (item) => handleEditPartClick(item, index),
                                                icon: () => PencilIcon
                                            },
                                            {
                                                title: () => translate('btn.delete'),
                                                action: (item) => handleDeletePartClick(item, index),
                                                icon: () => TrashIcon
                                            }
                                        ]}

                                        onRowClick={handleEditPartClick}
                                    />
                                </div>
                            )}

                            {!!dataLaborTable.length && (
                                <div className="space-y-4">
                                    <div className="text-base font-bold">Labor</div>

                                    <ResourceTable
                                        data={dataLaborTable}
                                        fields={getLaborFields({}, previewData)}

                                        translate={translate}
                                        options={
                                            {
                                                behaviour: {
                                                    rowSelect: false,
                                                    canAdjustWidth: false,
                                                    hasMenu: false

                                                },
                                                style: {
                                                    condensed: true,
                                                    isGPUAccelerated: true,
                                                    horizontalLines: true,
                                                    verticalLines: true
                                                }
                                            }
                                        }
                                        tfoot={{
                                            LaborHours: () => <div className="text-right font-bold">{dataLaborHoursTotal}</div>,
                                            LaborPrice: () => <div className="text-right font-bold">{genericMoneyFormatter(dataLaborPriceTotal)}</div>,
                                            TotalAmount: () => <div className="text-right font-bold">{genericMoneyFormatter(dataLaborTableTotal)}</div>
                                        }}
                                        actions={[
                                            {
                                                title: () => translate('btn.edit'),
                                                action: (item) => handleEditLaborClick(item, index),
                                                icon: () => PencilIcon
                                            },
                                            {
                                                title: () => translate('btn.delete'),
                                                action: (item) => handleDeleteLaborClick(item, index),
                                                icon: () => TrashIcon
                                            }
                                        ]}

                                        // onRowClick={handleEditLaborClick}
                                    />
                                </div>
                            )}
                        </div>
                    </Card>
                })}

                {!!miscData.length && (
                    <Card>
                        <div className={"flex justify-center text-lg"}>Misc. charges</div>

                        <ResourceTable
                            data={miscData}
                            fields={getMiscFields({})}

                            translate={translate}
                            options={
                                {
                                    behaviour: {
                                        rowSelect: false,
                                        canAdjustWidth: false,
                                        hasMenu: false
                                    },
                                    style: {
                                        condensed: true,
                                        isGPUAccelerated: true,
                                        horizontalLines: true,
                                        verticalLines: true
                                    }
                                }
                            }
                            tfoot={{
                                MiscQty: () => <div className="text-right font-bold">{miscQtyTotal}</div>,
                                MiscPrice: () => <div className="text-right font-bold">{genericMoneyFormatter(miscPriceTotal)}</div>,
                                TotalAmount: () => <div className="text-right font-bold">{genericMoneyFormatter(miscTotal)}</div>
                            }}
                            actions={[
                                {
                                    title: () => translate('btn.edit'),
                                    action: (item) => handleEditMiscClick(item),
                                    icon: () => PencilIcon
                                },
                                {
                                    title: () => translate('btn.delete'),
                                    action: (item) => handleDeleteMiscClick(item),
                                    icon: () => TrashIcon
                                }
                            ]}

                            onRowClick={handleEditMiscClick}
                        />
                    </Card>
                )}
            </div>
        }
        onClose={handleClose}
        file={ocrFile}
        onUploadDocument={handleUploadDocument}
        translate={translate}
        htmlAfter={
            <>
                <ModalSaveResource
                    show={isPartsFromDialogOpen}
                    initialFocusID="PartName"
                    fields={getPartsFields(selectedItem, null, previewData, () => setIsPartsFormDialogOpen(false), true)}
                    title={selectedItem ? translate("modal_heading.edit_part_item") : translate("modal_heading.add_part")}
                    onClose={() => {
                        setIsPartsFormDialogOpen(false);
                    }}
                    canSubmit={isPartsFromDirty}
                    handleInputChange={handlePartsInputChange}
                    onSubmit={(params) => {
                        if (params?.PartNameLabel) {
                            params.PartName = params.PartNameLabel;
                        }

                        if (!!params) {
                            if (selectedItem) {
                                let previewDataUpdate = Object.assign({}, previewData);

                                previewDataUpdate.Items[selectedItemGroupIndex]['Parts'][selectedItem.dataIndex] = params

                                setIsPartsFormDialogOpen(false);
                                setIsPartsFromDirty(true);
                                setPreviewData(previewDataUpdate);
                            }
                        }
                    }}
                    addFieldContainerClass={"col-span-6"}
                    addBodyClass={"pb-12 text-tm-gray-900"}
                    //resource={resource}
                    metadata={selects}
                    translate={translate}
                />

                <ModalSaveResource
                    show={isLaborFormDialogOpen}
                    initialFocusID="LaborPrice"
                    fields={getLaborFields(selectedItem, previewData)}
                    title={selectedItem?.TotalAmount ? translate("modal_heading.edit_labor_item") : translate("modal_heading.add_labor_item")}
                    onClose={() => {
                        setIsLaborFormDialogOpen(false);
                    }}
                    canSubmit={true}
                    handleInputChange={handleLaborInputChange}
                    onSubmit={(params) => {
                        if (!!params) {
                            let previewDataUpdate = Object.assign({}, previewData);
                            previewDataUpdate.Items[selectedItemGroupIndex]['Labor'][selectedItem.dataIndex] = params;
                            setIsLaborFormDialogOpen(false);
                            setIsPartsFromDirty(true);
                            setPreviewData(previewDataUpdate);
                        }
                    }}
                    addFieldContainerClass={"col-span-6"}
                    addBodyClass={"pb-12 text-tm-gray-900"}
                    metadata={selects}
                    translate={translate}
                />

                <ModalSaveResource
                    show={isMiscFormDialogOpen}
                    fields={getMiscFields(selectedItem)}
                    title={selectedItem ? translate("btn.edit_misc") : translate("btn.add_misc")}
                    onClose={() => {
                        setIsMiscFormDialogOpen(false);
                    }}
                    canSubmit={true}
                    handleInputChange={handleMiscInputChange}
                    onSubmit={(params) => {
                        if (!!params) {
                            let previewDataUpdate = Object.assign({}, previewData);
                            previewDataUpdate.Misc[selectedItem.dataIndex] = params;
                            setIsMiscFormDialogOpen(false);
                            setPreviewData(previewDataUpdate);
                        }
                    }}
                    addFieldContainerClass={"col-span-6"}
                    addBodyClass={"pb-12 text-tm-gray-900"}
                    metadata={selects}
                    translate={translate}
                />
            </>
        }
    />
}